import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { isEmpty } from "lodash-es";
import { useEffect, useState } from "react";

import { useBFFProductServiceApi } from "@/api/hooks/productService.hook";
import { ProductSliderSkeletonComponent } from "@/components/Product/ProductSlider/components/skeleton/productSliderSkeleton.component";
import { ProductSliderSourceContextProvider } from "@/components/Product/ProductSlider/context/productSliderSource.context";
import { filterVisible } from "@/components/Product/ProductSlider/productSlider.helpers";
import { RecentsContextProvider } from "@/components/Product/ProductSlider/sources/recents/context/recents.context";
import {
    getRecentlyViewed,
    removeItemFromRecentlyViewed,
    removeRecentlyViewed
} from "@/services/recentlyViewedProducts.service";

type RecentsProductsliderProps = {
    sku?: string;
    serverSideProducts?: Array<Product<ProductFieldSet.ProductTile>>;
};

export const RecentsProductslider = ({
    sku,
    children,
    serverSideProducts
}: RequireChildrenFor<RecentsProductsliderProps>) => {
    const [products, setProducts] = useState(serverSideProducts);

    const [{ loading, error }, loadProducts] = useBFFProductServiceApi<
        any,
        any,
        Array<Product<ProductFieldSet.ProductTile>>
    >(
        {
            url: `/products`,
            method: "GET"
        },
        {
            manual: true
        }
    );

    useEffect(() => {
        if (isDefined(products)) return;
        if (loading || error) return;

        const recentlyViewedSkus = getRecentlyViewed(sku);

        if (isEmpty(recentlyViewedSkus)) {
            setProducts([]);
        } else {
            loadProducts({
                params: {
                    sku: recentlyViewedSkus.join(","),
                    fields: ProductFieldSet.ProductTile
                }
            })
                .then(({ data }) => data)
                .then(filterVisible)
                .then(setProducts);
        }
    }, [sku, loadProducts, error, loading, products]);

    const deleteFromHistory = (sku: string) => {
        removeItemFromRecentlyViewed(sku);
        setProducts((prevSkus) => prevSkus?.filter((item) => item.sku !== sku));
    };

    const deleteRecentsHistory = () => {
        removeRecentlyViewed();
        setProducts([]);
    };

    if (error) {
        return null;
    } else if (isDefined(products) && isEmpty(products)) {
        return null;
    } else if (isDefined(products) && !isEmpty(products)) {
        return (
            <ProductSliderSourceContextProvider products={products}>
                <RecentsContextProvider
                    deleteFromHistory={deleteFromHistory}
                    deleteRecentsHistory={deleteRecentsHistory}
                >
                    {children}
                </RecentsContextProvider>
            </ProductSliderSourceContextProvider>
        );
    } else {
        return <ProductSliderSkeletonComponent />;
    }
};
